import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useCallback, useMemo } from 'react';
import { useRecordContext, useRefresh } from 'react-admin';

import { NumericOverrideField } from '@/components/NumericOverrideField';
import { useGetAppConfig } from '@/screens/therapistAppConfig/useGetAppConfig';

import { FeatureFlagTherapistRow } from './FeatureFlagTherapistRow';
import {
  DOMAIN_LABELS,
  groupFlagsByDomain,
  therapistOverridableFlags,
} from './registry';
import { FeatureFlagDomain, FlagEntry } from './types';

type Panel = {
  domain: FeatureFlagDomain;
  entries: FlagEntry[];
  proposedCount: number;
  matureCount: number;
};

const buildPanels = (): Panel[] => {
  const grouped = groupFlagsByDomain(therapistOverridableFlags);

  const panels: Panel[] = grouped.map(({ domain, entries }) => {
    const proposed = entries.filter(e => e.stability === 'proposed');
    const mature = entries.filter(e => e.stability === 'mature');
    return {
      domain,
      entries: [...proposed, ...mature],
      proposedCount: proposed.length,
      matureCount: mature.length,
    };
  });

  return [...panels].sort((a, b) => {
    const aActive = a.proposedCount > 0 ? 1 : 0;
    const bActive = b.proposedCount > 0 ? 1 : 0;
    return bActive - aActive;
  });
};

const summarySx = {
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper': {
    transform: 'rotate(-90deg)',
  },
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(0deg)',
  },
  '& .MuiAccordionSummary-content': {
    alignItems: 'center',
    gap: 1,
    marginLeft: '0.5em',
  },
} as const;

const detailsSx = {
  backgroundColor: 'rgba(255, 255, 255, 0.03)',
  padding: '1rem',
} as const;

type SectionProps = {
  panel: Panel;
  therapistId: string;
  therapistRecord: Record<string, unknown>;
  globalConfig: Record<string, unknown>;
  onAfterChange: () => void;
};

const PanelSection = ({
  panel,
  therapistId,
  therapistRecord,
  globalConfig,
  onAfterChange,
}: SectionProps) => {
  const fullyMature = panel.proposedCount === 0;
  const totalFlags = panel.entries.length;

  return (
    <Accordion defaultExpanded={!fullyMature} disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
        <Typography variant="subtitle1" fontWeight={600}>
          {DOMAIN_LABELS[panel.domain]}
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          color={fullyMature ? 'default' : 'primary'}
          label={`${totalFlags} flag${totalFlags === 1 ? '' : 's'}`}
        />
        {fullyMature ? (
          <Chip
            size="small"
            variant="outlined"
            color="default"
            label="All mature"
          />
        ) : null}
      </AccordionSummary>
      <AccordionDetails sx={detailsSx}>
        <Box display="flex" flexDirection="column">
          {panel.entries.map(entry => (
            <FeatureFlagTherapistRow
              key={entry.key}
              entry={entry}
              therapistId={therapistId}
              therapistRecord={therapistRecord}
              globalConfig={globalConfig}
              onAfterChange={onAfterChange}
            />
          ))}
          {panel.domain === 'transcription' ? (
            <Box marginTop={1.5}>
              <NumericOverrideField
                field="transcriptionSpeakerEnrollmentMinWordsPerSegment"
                label="Enrollment min words per diarized segment"
                helperText="Integer ≥1. Leave empty to clear override and use global app config (default 5)."
                inputProps={{ step: 1, min: 1 }}
              />
            </Box>
          ) : null}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export const TherapistFeatureFlagOverrides = () => {
  const record = useRecordContext();
  const refresh = useRefresh();
  const { remoteAppConfig } = useGetAppConfig();

  const panels = useMemo(buildPanels, []);

  const onAfterChange = useCallback(() => {
    refresh();
  }, [refresh]);

  if (!record) {
    return null;
  }

  const therapistId = String(record.id);
  const therapistRecord = record as unknown as Record<string, unknown>;

  if (!remoteAppConfig) {
    return (
      <Box display="flex" alignItems="center" gap={1} paddingY={2}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          Loading global feature flags…
        </Typography>
      </Box>
    );
  }

  const globalConfig = remoteAppConfig as Record<string, unknown>;

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Alert severity="info" variant="outlined">
        Per-therapist toggles can <strong>grant</strong> access when the
        global flag is off. They cannot revoke a feature that is enabled
        globally — a lock icon appears in that case and the control is
        disabled.
      </Alert>

      <Box display="flex" flexDirection="column">
        {panels.map(panel => (
          <PanelSection
            key={panel.domain}
            panel={panel}
            therapistId={therapistId}
            therapistRecord={therapistRecord}
            globalConfig={globalConfig}
            onAfterChange={onAfterChange}
          />
        ))}
      </Box>
    </Box>
  );
};
